import React, { useState } from "react";
import { Table, Button, Form, Input, Space, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { usePutPostAPI } from "utilFunctions/CustomHooks/usePutPostAPI";
import {
  BASE_URL,
  DEVELOPER_COMMON_CONFIG,
} from "utilFunctions/Constants/urlConstants";

export interface DRSConfig {
  id: number;
  configKey: string;
  configValue: string;
  displayValue: string;
}

interface DRSCustomConfigTableProps {
  configs: DRSConfig[];
  setConfigs: (group: string, updatedConfigs: DRSConfig[]) => void;
  groupName: string;
}

const DRSCustomConfigTable: React.FC<DRSCustomConfigTableProps> = ({
  configs,
  setConfigs,
  groupName,
}) => {
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [form] = Form.useForm();

  const { makeRequest: updateConfig, loading: saveLoading } = usePutPostAPI(
    (res, payload) => {
      const updatedItem = res?.data || res;
      const newConfigs = configs.map((item) =>
        item.id === editingKey
          ? {
              ...item,
              ...payload,
              ...(typeof updatedItem === "object" ? updatedItem : {}),
            }
          : item
      );
      setConfigs(groupName, newConfigs);
      setEditingKey(null);
      message.success("Configuration updated successfully!");
    },
    (error) => {
      console.log(error);
      message.error(error?.message || "Failed to update configuration.");
    },
    "PUT"
  );

  const isEditing = (record: DRSConfig) => record.id === editingKey;

  const edit = (record: DRSConfig) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey(null);
  };

  const save = async (id: number) => {
    try {
      const row = await form.validateFields();
      updateConfig(BASE_URL + DEVELOPER_COMMON_CONFIG + "/" + id, row);
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const columns: ColumnsType<DRSConfig> = [
    {
      title: "Display Name",
      dataIndex: "displayValue",
      key: "displayValue",
    },
    {
      title: "Config Key",
      dataIndex: "configKey",
      key: "configKey",
      render: (text) => <code style={{ fontSize: "12px" }}>{text}</code>,
    },
    {
      title: "Value",
      dataIndex: "configValue",
      key: "configValue",
      render: (text, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="configValue"
            style={{ margin: 0 }}
            rules={[{ required: true, message: "Please input value!" }]}
          >
            <Input />
          </Form.Item>
        ) : (
          text
        );
      },
    },
    {
      title: "Action",
      dataIndex: "operation",
      width: 150,
      render: (_, record: DRSConfig) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button
              type="primary"
              onClick={() => save(record.id)}
              loading={saveLoading}
              size="small"
            >
              Save
            </Button>
            <Button onClick={cancel} size="small">
              Cancel
            </Button>
          </Space>
        ) : (
          <Button
            disabled={editingKey !== null}
            onClick={() => edit(record)}
            size="small"
          >
            Edit
          </Button>
        );
      },
    },
  ];

  return (
    <Form form={form} component={false}>
      <Table
        rowKey="id"
        bordered
        dataSource={configs}
        columns={columns}
        pagination={false}
        size="small"
      />
    </Form>
  );
};

export default DRSCustomConfigTable;
